import '../styles/ImplementationPageBase.scss';

import { Button } from '@whistler-ui/k2/components/Button';
import { Empty } from '@whistler-ui/k2/components/Empty';
import { Stack } from '@whistler-ui/k2/components/Stack';
import type { TabMenuFunction } from '@whistler-ui/k2/components/Tabs';
import { Tabs } from '@whistler-ui/k2/components/Tabs';
import { Paragraph } from '@whistler-ui/k2/components/Typography';
import { useClassHelper } from '@whistler-ui/k2/hooks';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { ENTITLEMENTS_ROUTES } from '../../../constants';
import { useWebsheetConfigStore } from '../../../store';
import { BrandResponse } from '../../../types/websheets';
import { ImplementationBaseContainer } from '../../';
import { ImplementationPagePropsType } from '../types';
import { BasePagesSkeleton } from './BasePagesSkeleton';

const componentName = 'c-ImplementationPageBase';

interface BasePagesProps extends Omit<ImplementationPagePropsType, 'brand'> {
  tabMenuActions?: TabMenuFunction[];
  addNewBrand?: () => void;
  brands: BrandResponse[];
  isLoading?: boolean;
  activeBrand?: string;
  brandData: {
    data: any;
    isError: boolean;
    isPending: boolean;
    isSuccess: boolean;
  };
}

export const BasePages = ({
  addNewBrand = undefined,
  tabMenuActions = [],
  brandData,
  activeBrand,
  callbacks,
  pages,
  template,
  brands,
  tag,
  templateType,
  isLoading = false,
}: BasePagesProps) => {
  const c = useClassHelper(componentName);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['pages', 'common']);
  const { resetStore } = useWebsheetConfigStore();
  const { isError, isPending, isSuccess } = brandData;

  const handleClick = () => {
    if (addNewBrand) {
      return addNewBrand();
    }

    return navigate({
      pathname: location.pathname,
      search: `?sub-route=${ENTITLEMENTS_ROUTES.WEBSHEET_BRANDING.slug}`,
    });
  };

  // RENDER LOGIC
  if (isLoading || isPending) {
    return (
      <div className={c('__loading-wrapper')}>
        <BasePagesSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={c('__error-wrapper')}>
        <Empty.State title={t('common:errors.default')} />
      </div>
    );
  }

  if (isSuccess) {
    if (brands.length === 0) {
      return (
        <Stack direction="column" gap={4}>
          {addNewBrand ? (
            <Paragraph>
              {t('common:errors.no_brands_p1')}
              <br />
              {t('common:errors.no_brands_p2')}
            </Paragraph>
          ) : (
            <Paragraph>{t('common:errors.setup_brand')}</Paragraph>
          )}
          <div>
            <Button type="button" id={c('__button')} onClick={handleClick}>
              {addNewBrand
                ? t('common:action:buttons:add_primary_brand')
                : t('common:action:buttons:go_websheet')}{' '}
            </Button>
          </div>
        </Stack>
      );
    } else {
      return (
        <div style={{ position: 'relative' }}>
          <Tabs
            variant="unstyled"
            defaultActiveTab={activeBrand}
            tabMenuActions={tabMenuActions}
          >
            {brands.map(brand => {
              const { id, name } = brand;
              return (
                <Tabs.Item
                  title={name ? name : 'Brand Name'}
                  eventKey={id}
                  key={id}
                  onlyRenderOnActive
                  onClick={resetStore}
                >
                  <ImplementationBaseContainer
                    tag={tag}
                    brand={brand}
                    callbacks={callbacks}
                    pages={pages}
                    template={template}
                    templateType={templateType}
                  />
                </Tabs.Item>
              );
            })}
          </Tabs>
        </div>
      );
    }
  }

  return null;
};
